import { useMemo, useCallback, useRef, useEffect, useState } from "react";
import DownloadExcelButton from '../../../CommonComponents/common/DownloadExcelButton';
import DataTableWrapper from "../../../Components/CommonTableLayout/DataTableWrapper";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { Link } from "react-router-dom";
import { fetchInboundShipments, fetchFilterOptions } from "../../../services/InboundShipments/inboundServices";
import {
  getInitialFilterModel,
  createColumnFilterChangeHandler,
  createCascadingFilterParams,
  createDateFilterParams,
  sanitizeRootFilters
} from "../../../utils/columnFilterUtils";
import { Box } from "@mui/material";

const DEFAULT_PAGE_SIZE = 25;
const FILTER_CACHE_KEY = 'supply_shipment';

const InboundShipmentTable = ({ filters, setRowCount, onColumnFilterChanged }) => {


  const [currentFilters, setCurrentFilters] = useState({});
  const initialFilterModel = useMemo(() => getInitialFilterModel(FILTER_CACHE_KEY), []);
  const fetchCacheRef = useRef(new Map());
  const debounceTimersRef = useRef({});

  const mapFiltersForApi = useCallback((columnFilters) => {
    const mapped = { appliedFilters: {} };
    Object.entries(columnFilters || {}).forEach(([key, filterObj]) => {
      if (key !== 'state' && key !== 'exceptionType') {

        // CASE 1: Stored format { filterType, values }
        if (filterObj?.filterType === 'date') {

          mapped.appliedFilters[key] = {
            filterType: 'date',
            ...filterObj.values
          };
        }

        else if (filterObj?.filterType) {
          // Keep set/text as-is
          mapped.appliedFilters[key] = {
            filterType: filterObj.filterType,
            values: filterObj.values
          };
        }
        
        else if (filterObj?.type) {
          const dateTypes = new Set(['equals', 'notEqual', 'lessThan', 'greaterThan', 'inRange', 'blank', 'notBlank']);

          if (dateTypes.has(filterObj.type)) {

            mapped.appliedFilters[key] = {
              filterType: 'date',
              ...filterObj
            }
          } else {
            mapped.appliedFilters[key] = {
              filterType: 'text',
              values: filterObj
            };
          }
        }

        // CASE 3: Set array
        else if (Array.isArray(filterObj)) {
          mapped.appliedFilters[key] = {
            filterType: 'set',
            values: filterObj
          };
        }

      }
    });

    if (filters?.states && filters.states.length > 0) {
      mapped.selectedStates = filters.states;
    }
    if (filters?.exceptions && filters.exceptions.length > 0) {
      mapped.selectedExceptions = filters.exceptions;
    }

    return mapped;
  }, [filters]);


  const fetchOptionsStable = useCallback((col, columnFilters) => {
    const apiPayload = mapFiltersForApi(columnFilters);
    const key = `${col}::${JSON.stringify(apiPayload)}`;
    if (fetchCacheRef.current.has(key)) {
      return fetchCacheRef.current.get(key);
    }

    let resolveFn, rejectFn;
    const p = new Promise((res, rej) => { resolveFn = res; rejectFn = rej; });
    fetchCacheRef.current.set(key, p);

    if (debounceTimersRef.current[key]) clearTimeout(debounceTimersRef.current[key]);
    debounceTimersRef.current[key] = setTimeout(async () => {
      try {
        const res = await fetchFilterOptions(col, apiPayload);
        resolveFn(res);
      } catch (err) {
        fetchCacheRef.current.delete(key);
        rejectFn(err);
      } finally {
        delete debounceTimersRef.current[key];
      }
    }, 120);

    return p;
  }, [mapFiltersForApi]);


  const handleColumnFilterChanged = useCallback((columnFilters, rawFilterModel) => {
    try {
      // Save enriched AG Grid model for UI restoration
      if (rawFilterModel) {
        const enrichedRaw = Object.fromEntries(
          Object.entries(rawFilterModel || {}).map(([field, model]) => {
            let filterType = model?.filterType;
            if (!filterType) {
              if (Array.isArray(model?.values)) {
                filterType = 'set';
              } else if (model?.type) {
                const dateTypes = new Set(['equals', 'notEqual', 'lessThan', 'greaterThan', 'inRange', 'blank', 'notBlank']);
                filterType = dateTypes.has(model.type) ? 'date' : 'text';
              } else {
                filterType = 'unknown';
              }
            }
            return [field, { ...model, filterType }];
          })
        );
        localStorage.setItem(FILTER_CACHE_KEY, JSON.stringify(enrichedRaw));
      }
    } catch (error) {
      console.error('Error saving filters:', error);
    }

    setCurrentFilters(columnFilters);

    if (typeof onColumnFilterChanged === 'function') {
      onColumnFilterChanged(columnFilters);
    }

    try {
      const pageKey = FILTER_CACHE_KEY;
      const simplifiedWithTypes = Object.fromEntries(
        Object.entries(columnFilters || {}).map(([field, value]) => {
          let filterType = undefined;
          const raw = rawFilterModel?.[field];
          if (raw?.filterType) {
            filterType = raw.filterType;
          } else if (Array.isArray(raw?.values)) {
            filterType = 'set';
          } else if (raw?.type) {
            const dateTypes = new Set(['equals', 'notEqual', 'lessThan', 'greaterThan', 'inRange', 'blank', 'notBlank']);
            filterType = dateTypes.has(raw.type) ? 'date' : 'text';
          }
          return [field, { filterType, values: value }];
        })
      );
      localStorage.setItem(`columnFilters_${pageKey}`, JSON.stringify(simplifiedWithTypes));
      window.dispatchEvent(new CustomEvent(`columnFiltersChanged_${pageKey}`));
    } catch (err) {
      console.warn('Failed to persist/notify column filter change', err);
    }
  }, [onColumnFilterChanged]);

  const columns = useMemo(
    () => {
      return [
        {
          field: "shipmentNo",
          headerName: "Shipment No",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'shipmentNo',
            currentFilters
          ),
          cellRenderer: (params) => {
            let borderColor = "transparent";
            switch (params.data?.exceptionType) {
              case "Untracked": borderColor = "#e41c24"; break;
              case "Late": borderColor = "#ff560e"; break;
              case "Early": borderColor = "#bc6bd9"; break;
              case "Delayed": borderColor = "#f79900"; break;
              case "PTA UNKNOWN": borderColor = "#e5813e"; break;
              case "On Time": borderColor = "#268000"; break;
              default: borderColor = "black";
            }
            return (
              <div style={{ borderLeft: `5px solid ${borderColor}`, paddingLeft: 8, display: "flex", alignItems: "center" }}>
                <LocalShippingOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                <Link
                  to={`shipment-detail/${params.value}`}
                  style={{ color: "#1976d2", textDecoration: "underline", cursor: "pointer" }}
                  state={{ menu: "Supply", label: "Inbound Shipments", outerLabel: "Inbound Shipments Detail" }}
                >
                  {params.value}
                </Link>
              </div>
            );
          }
        },
        {
          field: "supplier",
          headerName: "Supplier",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'supplier',
            currentFilters
          )
        },
        {
          field: "customer",
          headerName: "Customer",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'customer',
            currentFilters
          )
        },
        {
          field: "carrier",
          headerName: "Carrier",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'carrier',
            currentFilters
          )
        },
        {
          field: "plannedShipDate",
          headerName: "Planned Ship Date",
          filter: 'agDateColumnFilter',
          filterParams: createDateFilterParams()
        },
        {
          field: "containerETA",
          headerName: "ETA",
          filter: 'agDateColumnFilter',
          filterParams: createDateFilterParams()
        },
        {
          field: "creationDate",
          headerName: "Creation Date",
          filter: 'agDateColumnFilter',
          filterParams: createDateFilterParams()
        },
        {
          field: "lastModifiedDate",
          headerName: "Last Modified Date",
          filter: 'agDateColumnFilter',
          filterParams: createDateFilterParams()
        },
      ];
    },
    [currentFilters, fetchOptionsStable]
  );

  const fetchRows = useCallback(
    async ({ page, size, sort }) => {
      try {
        const validatedFilters = sanitizeRootFilters(filters, columns);

        const { rows, totalElements } = await fetchInboundShipments({
          page,
          size,
          filters: validatedFilters,
          sort,
        });

        if (setRowCount) setRowCount(totalElements);
        return { rows, totalElements };
      } catch {
        return { rows: [], totalElements: 0 };
      }
    },
    [filters, setRowCount, columns]
  );


  return (

    <div style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', top: 0, right: -14, zIndex: 2, margin: 8 }}>
        <DownloadExcelButton
          endpoint={(process.env.REACT_APP_BASE_URL || '') + 'supply/shipments'}
          payload={{ ...filters }}
          params={{ page: 0, size: 100, download: 'true' }}
          defaultFilename="InboundShipments.xlsx"
        />
      </div>
      <Box sx={{
        height: "calc(100vh - 255px)",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
        boxSizing: "border-box",
        pb: 1
      }}>
        <DataTableWrapper
          title="Shipments"
          columns={columns}
          filters={filters}
          height={500}
          fetchRows={fetchRows}
          rowCount={undefined}
          defaultPageSize={DEFAULT_PAGE_SIZE}
          onColumnFilterChanged={handleColumnFilterChanged}
          initialFilterModel={initialFilterModel}
        />
      </Box>
    </div>

  );
};

export default InboundShipmentTable;